import React, { useEffect, useState } from 'react'

import { Video } from 'component-library/content/Video/Video'

import {
    responsiveComponent,
    ResponsiveProps,
} from 'component-library/__helpers/responsive-component'
import { withDimensions, DimensionProps } from 'component-library/__helpers/with-dimensions'
import { breakpoints } from 'component-library/__styling/settings/breakpoints'
import { Props } from 'component-library/publication/ArticleVideo/ArticleVideo'

import {
    StyledArticleVideo,
    StyledArticleVideoContainer,
    StyledFigcaption,
} from 'component-library/publication/ArticleVideo/ArticleVideo.styled'
import { PlayerEvent } from 'common/events/video-event-types'
import { ConsentLevel } from 'common/data/consent/consent.redux'
import { useConsentGiven } from 'common/hooks/useConsentGiven'
import { ConsentFallback } from 'component-library/tracking/ConsentFallback/ConsentFallback'

export type PropsWithResponsive = Props & ResponsiveProps & DimensionProps

const UnwrappedArticleVideo: React.FC<PropsWithResponsive> = props => {
    const [isCurrentlySticky, setIsCurrentlySticky] = useState(false)
    // P19-703 We only want sticky videos if autoplay is on or the player has been interacted with (played)
    const [userInteracted, setUserInteracted] = useState(props.autoplayOptions ? true : false)

    const canLoadVideoPlayer = useConsentGiven({
        consentRequiredLevel: ConsentLevel.Advertising | ConsentLevel.Analytics,
    })

    const handleUserInteraction = (e: PlayerEvent) => {
        if (e.type === 'video.play') {
            setUserInteracted(true)
        }
        props.onEvent(e)
    }

    useEffect(() => {
        const detectSticky = (entries: IntersectionObserverEntry[]) => {
            // Setup the sticky behaviour - sticky should be applied to the container
            // not the video directly because position:sticky sticks items to
            // their parent container not the viewport.

            const stickyElement = document.querySelector('.sticky-cell') as HTMLElement

            // cast to boolean because Sticky.isActive defaults to true
            const stickyActive = !!(
                props.viewportWidth >= breakpoints.md &&
                // P19-703 We only want sticky videos if autoplay is on or the player has been interacted with (played)
                userInteracted
            )

            if (stickyActive && stickyElement && entries[0].isIntersecting === false) {
                // If intersectionRatio is 0, the target is out of view
                stickyElement.classList.add('is-sticky')
                setIsCurrentlySticky(true)
            } else {
                stickyElement && stickyElement.classList.remove('is-sticky')
                setIsCurrentlySticky(false)
            }
        }

        let stickyObserver: IntersectionObserver | undefined
        const options = {
            rootMargin: '350px 0px 0px 0px',
            threshold: 1.0,
        }

        if (props.isVideoStickyEnabled && 'IntersectionObserver' in window) {
            const stickyElement = document.querySelector('.sticky-cell') as HTMLElement

            // Set height of container so that it doesn't jank around when the width/height changes
            stickyElement &&
                stickyElement.style.setProperty(
                    '--stickyHeight',
                    `${stickyElement.getBoundingClientRect().height}px`
                )

            // eslint-disable-next-line compat/compat
            stickyObserver = new IntersectionObserver(detectSticky, options)

            const stickyWatcher = document.querySelector('.sticky-top')

            stickyWatcher && stickyObserver.observe(stickyWatcher)
        }
        return () => {
            if (stickyObserver) {
                stickyObserver.disconnect()
            }
        }
    }, [])

    const {
        byline,
        captionText,
        credit,
        kind = 'video',
        videoDetails,
        isHeroVideo,
        autoplayOptions,
        autoplayNextOptions,
        figcaptionRenderOptions,
    } = props

    if (!videoDetails.id) {
        return null
    }

    if (!canLoadVideoPlayer) {
        return <ConsentFallback />
    }

    const videoElement = (
        <Video
            videoDetails={videoDetails}
            showTitles={props.showTitles}
            pauseOverlay={props.pauseOverlay}
            autoplayOptions={autoplayOptions}
            autoplayNextOptions={autoplayNextOptions}
            videoOptions={{
                ...props.videoOptions,
                compactMode: isCurrentlySticky || props.videoOptions.compactMode,
            }}
            innerRef={props.innerRef}
            getVideoQueue={props.getVideoQueue}
            onEvent={props.onEvent}
            pagePosition={props.isHeroVideo ? 'hero-video' : 'inline-article'}
            onUserInteraction={handleUserInteraction}
            showPlaylist={props.showPlaylist && !isCurrentlySticky}
        />
    )

    return (
        <StyledArticleVideo isHeroVideo={isHeroVideo} className="article-video">
            <StyledArticleVideoContainer isHeroVideo={isHeroVideo}>
                {videoElement}
            </StyledArticleVideoContainer>

            {figcaptionRenderOptions !== 'hidden' && (captionText || credit || byline) && (
                <StyledFigcaption
                    byline={byline}
                    captionText={captionText}
                    credit={credit}
                    isHeroVideo={isHeroVideo}
                    kind={kind}
                    figcaptionLayout={figcaptionRenderOptions}
                />
            )}
        </StyledArticleVideo>
    )
}

export const WebArticleVideo = responsiveComponent<Props>(
    withDimensions<Props & ResponsiveProps>(UnwrappedArticleVideo)
)
export default WebArticleVideo
